import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Landmark, FileCheck, Globe2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "./data";

const ICONS = [Landmark, FileCheck, Globe2];

export const Framework = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="framework" ref={ref} data-testid="framework-section"
      className="relative overflow-hidden py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div className="relative order-2 lg:order-1">
          <div className="relative overflow-hidden border border-gold/20">
            <motion.img src={IMAGES.columns} alt="Classical courthouse columns"
              style={{ y: imgY }} className="h-[420px] w-full scale-110 object-cover lg:h-[560px]" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-2 hidden border border-gold/30 bg-navy-deep px-6 py-5 lg:-right-6 lg:block">
            <div className="font-serif text-4xl font-semibold text-gold">Art. III</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              {t.framework.artLabel}
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <Reveal><Overline>{t.framework.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {t.framework.titleA} <span className="italic text-gold-gradient">{t.framework.titleB}</span>{t.framework.titleC}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-xl leading-relaxed text-slate-400">{t.framework.body}</p>
          </Reveal>
          <div className="mt-10 space-y-8">
            {t.framework.pillars.map((p, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={p.title} delay={0.22 + i * 0.08}>
                  <div className="group flex gap-5 border-l border-navy-border pl-6 transition-colors duration-500 hover:border-gold" data-testid={`framework-pillar-${i}`}>
                    <Icon className="mt-1 h-6 w-6 shrink-0 text-gold" strokeWidth={1.25} />
                    <div>
                      <h3 className="font-serif text-xl font-medium text-slate-100">{p.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
